<template lang="pug">
  v-flex(xs12 md4)
    v-layout(row wrap)
      v-flex(xs12)
        div.headline.pb-5 Select Type of Action
      v-flex(v-for="actionType in actionTypes" :key="actionType.text" xs6 data-cy="select-type-action")
        selection-tile(
          :data-cy="`selection-tile-${actionType.key}`"
          :isActive="value && value.key === actionType.key"
          :textContent="actionType.text"
          :icon="actionType.icon"
          :disabled="disableActionType(actionType)"
          :warning="actionTypeWarning(actionType)"
          @click.native="setActionType(actionType)"
        )

      liondesk-dialog(
        v-model="showWarning"
        :dialog-title="hasMlsUserAccessToTextFeature ? 'Upgrade to use lead assist' : 'Campaign Action Type Conflict'"
        ok-button-text="Close"
        @click:ok="showWarning = false"
        :hide-cancel-button="true"
      )
        template(slot="body" v-if="hasMlsUserAccessToTextFeature")
          div.mb-3
            | Lead Assist is not available with your
            strong &nbsp;MLS Plan.&nbsp;
            | Upgrade to a
            strong &nbsp;paid plan&nbsp;
            | to access other paid features, including:
          div
            ol
              li
                span Texts
              li
                span Video text
        template(slot="body" v-else)
          div(v-if="isLeadAssistConnected" data-cy="action-type-warning")
            div
              | A
              strong &nbsp;{{ conflictActionType }}&nbsp;
              | action cannot be added to a campaign containing an existing Lead Assist action or text action(s).

            div.mt-3
              | Your campaign
              strong &nbsp;{{ campaignName }}&nbsp;
              | already contains:
              ul
                li(v-if="leadAssistActions.length") {{ leadAssistActions.length }} lead assist action
                li(v-if="textActions.length") {{ textActions.length }} text action(s)

          div(v-else data-cy="connection-warning")
            | Please&nbsp;
            a.font-weight-bold(
              :href="leadAssistAuthUrl"
              ) Connect to Lead Assist
            | &nbsp;before selecting this option
    div.copy-box(v-if="value.key === 'text'") Ensure all of your contacts have provided consent prior to texting them. You can learn more about SMS consent best practices and tips 
      a.font-weight-bold.accent--text(
        href='https://lonewolf.my.site.com/s/article/LD-Best-practices-for-texting-in-LionDesk'
        target='_blank'
        ) here.
</template>
<script>
import SelectionTile from '@/Core/components/SelectionTile';
import LiondeskDialog from '@/Core/components/LiondeskDialog';
import { leadAssistAuthUrl } from '@/Campaigns/utilities';
import { mapGetters } from 'vuex';

export default {
  components: {
    LiondeskDialog,
    SelectionTile,
  },

  props: {
    isLeadAssistConnected: Boolean,
    campaignName: String,
    /**
     * pass in existing list of campaign actions to check
     * if certain campaign action types can be selected or not
     */
    currentCampaignActions: Array,
    /**
     * The possible action types. Each action type is represented as:
     * {
     *  key: 0, // integer
     *  text: 'Email', // String, the desired text to be displayed
     *  icon: 'email', // String, the desired icon to be displayed
     *  enabled: true, // Boolean, Whether the desired tile is disabled
     * }
     */
    actionTypes: Array,
    /**
     * The Selected Action Type. Expects an object in the following structure:
     * {
     *  key: 0, // number
     *  text: 'email', // string
     *  icon: 'email', // optional icon
     *  enabled: true, // Boolean, Whether the desired tile is disabled
     * }
     */
    value: Object,
  },
  data() {
    return {
      leadAssistType: 'LionDesk Lead Assist (TM)',
      textType: 'Text Contact',
      showWarning: false,
      // show error message based on the action type that has a conflict
      conflictActionType: '',
    };
  },

  computed: {
    ...mapGetters({
      hasMlsUserAccessToTextFeature: 'userInformation/hasMlsUserAccessToTextFeature',
    }),
    textActions() {
      return this.currentCampaignActions.filter(action => {
        return action.type === this.textType;
      });
    },
    leadAssistActions() {
      return this.currentCampaignActions.filter(action => {
        return action.type === this.leadAssistType;
      });
    },
    isEditing() {
      return !!this.$route.params.actionId;
    },
    canChangeToLeadAssist() {
      return this.isEditing && this.textActions.length === 1 && this.value.key === 'text';
    },
    canChangeToTextAction() {
      return this.isEditing && this.value.key === 'lead_assist';
    },
    leadAssistAuthUrl,
  },
  methods: {
    disableActionType(actionType) {
      return actionType.disabled;
    },
    // display warning tooltip message in selection tiles
    actionTypeWarning(actionType) {
      if (this.value.key === actionType.key) return;
      const hasLeadAssist = this.leadAssistActions.length;
      const hasText = this.textActions.length;

      switch (actionType.key) {
        case 'text':
          if (this.canChangeToTextAction) return;
          if (hasLeadAssist) {
            return 'Cannot select this option if your campaign already has a lead assist action';
          }
          break;
        case 'lead_assist':
          if (this.canChangeToLeadAssist) return;
          if (this.hasMlsUserAccessToTextFeature) {
            return 'Cannot select this option - Lead Assist is not available with your MLS Plan';
          }
          if (!this.isLeadAssistConnected) {
            return 'Cannot select this option - No Lead Assist connection detected';
          }

          if (hasText || hasLeadAssist) {
            return 'Cannot select this option if your campaign already has text or lead assist action(s)';
          }
          break;
        default:
          return null;
      }
    },
    /**
      * Check that the current campaign does not already contain a
      * text/another lead assist campaign action
      * Alternatively, if a lead assist task exists, prevent user from
      * creating a Text campaign action
      *
      * Allow user to select the action type if returns true
      */
    canSelectActionType(actionType) {
      let numActionTypeConflicts = 0;
      switch (actionType.key) {
        case 'lead_assist': {
          if (this.value.key === actionType.key) break;
          if (!this.isLeadAssistConnected || this.hasMlsUserAccessToTextFeature) return false;
          if (this.canChangeToLeadAssist) {
            /**
             * allow user to change a text action to lead assist if there's only 1 text action
             * in the campaign
             */
            numActionTypeConflicts += this.leadAssistActions.length;
            break;
          }
          numActionTypeConflicts += (this.leadAssistActions.length + this.textActions.length);
          break;
        }
        case 'text': {
          // allow user to change a lead assist action to text action
          if (this.canChangeToTextAction) break;
          numActionTypeConflicts += this.leadAssistActions.length;
          break;
        }
        default:
          return true;
      }
      if (numActionTypeConflicts) {
        this.conflictActionType = actionType.text;
      }
      return numActionTypeConflicts === 0;
    },
    setActionType(value) {
      const validActionType = this.canSelectActionType(value);
      if (!value.disabled && validActionType) {
        this.$emit('input', value);
        return;
      }

      // don't show warning when user click on disabled action type icons
      if (!validActionType) this.showWarning = true;
    },
  },
};
</script>

<style lang="scss" scoped>
ol {
  padding: 0 16px;

  li {
    list-style-type: none;
    display: inline-block;
    min-width: 250px;
    min-height: 35px;

    &:before {
      font-family: 'Material Icons';
      font-weight: normal;
      content: 'check_circle';
      color: var(--v-background-base);
      padding: 2px 4px;
      vertical-align: middle;
    }
  }
}
</style>
